import React, { Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Box, CircularProgress } from '@mui/material'

import ProtectedRoute from './ProtectedRoute'
import RequireAccess from './RequireAccess'
import { APP_KEYS } from '@/config/appKeys'
import PublicRoute from './PublicRoute'

// Lazy load pages for better performance
const LoginPage = React.lazy(() => import('@pages/LoginPage'))
const HomePage = React.lazy(() => import('@pages/HomePage'))
const ChatPage = React.lazy(() => import('@pages/ChatPage'))
const KnowledgePage = React.lazy(() => import('@pages/KnowledgePage'))
const UserManagementPage = React.lazy(() => import('@pages/UserManagementPage'))
const ProfilePage = React.lazy(() => import('@pages/ProfilePage'))
const CandidateMatchingPage = React.lazy(() => import('@pages/CandidateMatchingPage'))
const UploadCandidatesPage = React.lazy(() => import('@pages/UploadCandidatesPage'))
const ManagePositionsPage = React.lazy(() => import('@pages/ManagePositionsPage'))
const PositionEditPage = React.lazy(() => import('@pages/PositionEditPage'))
const CanteraDashboardPage = React.lazy(() => import('@pages/CanteraDashboardPage'))
const ProjectsPage = React.lazy(() => import('@pages/ProjectsPage'))
const ProjectDetailPage = React.lazy(() => import('@pages/ProjectDetailPage'))
const ClientsPage = React.lazy(() => import('@pages/ClientsPage'))
const ClientDetailPage = React.lazy(() => import('@pages/ClientDetailPage'))
const EmployeeListPage = React.lazy(() => import('@pages/EmployeeListPage'))  // Phase 113 M3 roster
const EmployeeDetailPage = React.lazy(() => import('@pages/EmployeeDetailPage'))  // Phase 113 M5 detail
const PlatformDashboardPage = React.lazy(() => import('@pages/PlatformDashboardPage'))
const PermissionsPage = React.lazy(() => import('@pages/PermissionsPage'))
const BanruralDashboardPage = React.lazy(() => import('@pages/BanruralDashboardPage'))
const BanruralExtractionPage = React.lazy(() => import('@pages/BanruralExtractionPage'))
const BanruralAnalysisPage = React.lazy(() => import('@pages/BanruralAnalysisPage'))
const TiposCreditoPage = React.lazy(() => import('@pages/TiposCreditoPage'))
const TiposDestinoPage = React.lazy(() => import('@pages/TiposDestinoPage'))
const ReglasAprobacionPage = React.lazy(() => import('@pages/ReglasAprobacionPage'))
const TiposCuotasPage = React.lazy(() => import('@pages/TiposCuotasPage'))

const LoadingSpinner: React.FC = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
  >
    <CircularProgress size={40} />
  </Box>
)

const AppRoutes: React.FC = () => {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />

        {/* Handle /index.html access - redirect to home page */}
        <Route path="/index.html" element={<ProtectedRoute />}>
          <Route path="" element={<Navigate to="/" replace />} />
        </Route>

        {/* Protected Routes — all routes inside ProtectedRoute get auth + boot screen + Layout */}
        <Route path="/" element={<ProtectedRoute />}>
          {/* Infrastructure — no app gating, just auth + boot */}
          <Route path="" element={<HomePage />} />
          <Route path="profile" element={<ProfilePage />} />

          {/* Phase 76: Shared apps — gated by app access */}
          <Route path="chat" element={<RequireAccess app={APP_KEYS.COMPAS}><ChatPage /></RequireAccess>} />
          <Route path="chat/:conversationId" element={<RequireAccess app={APP_KEYS.COMPAS}><ChatPage /></RequireAccess>} />
          {/* RE-ENABLED 2026-02-05: Phase 68 local testing (BUG-001 fixed) */}
          <Route path="talent-pipeline" element={<RequireAccess app={APP_KEYS.CANTERA}><CandidateMatchingPage /></RequireAccess>} />
          <Route path="talent-pipeline/tablero" element={<RequireAccess app={APP_KEYS.CANTERA}><CanteraDashboardPage /></RequireAccess>} />
          <Route path="talent-pipeline/upload" element={<RequireAccess app={APP_KEYS.CANTERA}><UploadCandidatesPage /></RequireAccess>} />
          <Route path="talent-pipeline/positions" element={<RequireAccess app={APP_KEYS.CANTERA}><ManagePositionsPage /></RequireAccess>} />
          <Route path="talent-pipeline/positions/:id/edit" element={<RequireAccess app={APP_KEYS.CANTERA}><PositionEditPage /></RequireAccess>} />
          <Route path="talent-pipeline/projects" element={<RequireAccess app={APP_KEYS.CANTERA}><ProjectsPage /></RequireAccess>} />
          <Route path="talent-pipeline/projects/:projectId" element={<RequireAccess app={APP_KEYS.CANTERA}><ProjectDetailPage /></RequireAccess>} />
          <Route path="talent-pipeline/clients" element={<RequireAccess app={APP_KEYS.CANTERA}><ClientsPage /></RequireAccess>} />
          <Route path="talent-pipeline/clients/:clientId" element={<RequireAccess app={APP_KEYS.CANTERA}><ClientDetailPage /></RequireAccess>} />
          {/* Phase 113 — Colaboradores (employees) roster + detail; same CANTERA gate as peers */}
          <Route path="talent-pipeline/colaboradores" element={<RequireAccess app={APP_KEYS.CANTERA}><EmployeeListPage /></RequireAccess>} />
          <Route path="talent-pipeline/colaboradores/:employeeId" element={<RequireAccess app={APP_KEYS.CANTERA}><EmployeeDetailPage /></RequireAccess>} />

          {/* Phase 80: Banrural module */}
          <Route path="banrural" element={<RequireAccess app={APP_KEYS.BANRURAL}><BanruralDashboardPage /></RequireAccess>} />
          <Route path="banrural/extraccion" element={<RequireAccess app={APP_KEYS.BANRURAL}><BanruralExtractionPage /></RequireAccess>} />
          <Route path="banrural/analisis/:caseId" element={<RequireAccess app={APP_KEYS.BANRURAL}><BanruralAnalysisPage /></RequireAccess>} />
          <Route path="banrural/tipos-credito" element={<RequireAccess app={APP_KEYS.BANRURAL}><TiposCreditoPage /></RequireAccess>} />
          <Route path="banrural/tipos-credito/:tipoCreditoId/destinos" element={<RequireAccess app={APP_KEYS.BANRURAL}><TiposDestinoPage /></RequireAccess>} />
          <Route path="banrural/tipos-credito/:tipoCreditoId/reglas-aprobacion" element={<RequireAccess app={APP_KEYS.BANRURAL}><ReglasAprobacionPage /></RequireAccess>} />
          <Route path="banrural/tipos-cuotas" element={<RequireAccess app={APP_KEYS.BANRURAL}><TiposCuotasPage /></RequireAccess>} />

          {/* Phase 76: Admin apps — previously under AdminRoute, now gated by app access */}
          <Route path="knowledge" element={<RequireAccess app={APP_KEYS.KNOWLEDGE}><KnowledgePage /></RequireAccess>} />
          <Route path="admin/users" element={<RequireAccess app={APP_KEYS.USERS}><UserManagementPage /></RequireAccess>} />
          <Route path="admin/permisos" element={<RequireAccess app={APP_KEYS.PERMISOS}><PermissionsPage /></RequireAccess>} />
          <Route path="admin/plataforma" element={<RequireAccess app={APP_KEYS.PLATAFORMA}><PlatformDashboardPage /></RequireAccess>} />
        </Route>

        {/* 404 Route - Redirect to HomePage */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}

export default AppRoutes
